import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import type { AdResult } from '../mocks/adResults';

export interface EditEntry {
  prompt: string;
  url: string;
}

const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function makeKey(userId: string) {
  return `adgen_session_v2_${userId}`;
}

// Strip large base64 blobs before persisting to localStorage
function stripHeavyData(result: AdResult): AdResult {
  const { imageBase64s: _b64s, imageBase64: _b64, ...rest } = result;
  if (rest.imageStorageUrls && rest.imageStorageUrls.length > 0) {
    return {
      ...rest,
      imageUrls: rest.imageStorageUrls,
      imageUrl: rest.imageStorageUrls[0] ?? rest.imageUrl,
    };
  }
  return rest;
}

interface PersistedSession {
  result: AdResult | null;
  editedUrls: Record<number, string>;
  editHistory: Record<number, EditEntry[]>;
  generationId: string | null;
}

export function useAdSession() {
  const { user } = useAuth();
  const userId = user?.id ?? 'anon';
  const storageKey = makeKey(userId);

  const [result, setResultInternal] = useState<AdResult | null>(null);
  const [editedUrls, setEditedUrls] = useState<Record<number, string>>({});
  const [editHistory, setEditHistory] = useState<Record<number, EditEntry[]>>({});
  const [generationId, setGenerationIdInternal] = useState<string | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);

  // Restore from localStorage on mount / user change
  useEffect(() => {
    setResultInternal(null);
    setEditedUrls({});
    setEditHistory({});
    setGenerationIdInternal(null);
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const { session, ts } = JSON.parse(raw) as { session: PersistedSession; ts: number };
        if (Date.now() - ts < SESSION_TTL_MS) {
          if (session.result) setResultInternal(session.result);
          if (session.editedUrls) setEditedUrls(session.editedUrls);
          if (session.editHistory) setEditHistory(session.editHistory);
          if (session.generationId) setGenerationIdInternal(session.generationId);
        } else {
          localStorage.removeItem(storageKey);
        }
      }
    } catch {
      // ignore malformed data
    }
    setSessionLoaded(true);
  }, [storageKey]);

  // Persist whenever state changes (after initial load)
  useEffect(() => {
    if (!sessionLoaded) return;
    try {
      const session: PersistedSession = {
        result: result ? stripHeavyData(result) : null,
        editedUrls,
        editHistory,
        generationId,
      };
      localStorage.setItem(storageKey, JSON.stringify({ session, ts: Date.now() }));
    } catch {
      // localStorage quota exceeded — silently ignore
    }
  }, [result, editedUrls, editHistory, generationId, sessionLoaded, storageKey]);

  /** Set a fresh result (new generation). Resets all edit state. */
  const setResult = useCallback((r: AdResult | null) => {
    setResultInternal(r);
    setEditedUrls({});
    setEditHistory({});
    // generationId is set separately via setGenerationId
  }, []);

  /** Update only the copy fields without resetting image edit state */
  const updateResultCopy = useCallback((copyData: Partial<Pick<AdResult, 'hooks' | 'headlines' | 'subheadlines' | 'primaryText' | 'ctas' | 'overlayText'>>) => {
    setResultInternal(prev => prev ? { ...prev, ...copyData } : null);
  }, []);

  /** Set the Supabase ad_generations row ID for the current session */
  const setGenerationId = useCallback((id: string | null) => {
    setGenerationIdInternal(id);
  }, []);

  /** Update the edited URL for a specific creative index */
  const updateEditedUrl = useCallback((idx: number, url: string) => {
    setEditedUrls((prev) => ({ ...prev, [idx]: url }));
  }, []);

  /** Replace the entire edit history array for a specific creative index */
  const updateEditHistory = useCallback((idx: number, entries: EditEntry[]) => {
    setEditHistory((prev) => ({ ...prev, [idx]: entries }));
  }, []);

  /** Revert a specific image back to its original generated version */
  const revertToOriginal = useCallback((idx: number) => {
    setEditedUrls((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
    setEditHistory((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  }, []);

  /** Wipe the entire session (new campaign) */
  const clearSession = useCallback(() => {
    setResultInternal(null);
    setEditedUrls({});
    setEditHistory({});
    setGenerationIdInternal(null);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  }, [storageKey]);

  return {
    result,
    setResult,
    updateResultCopy,
    editedUrls,
    updateEditedUrl,
    editHistory,
    updateEditHistory,
    revertToOriginal,
    clearSession,
    sessionLoaded,
    generationId,
    setGenerationId,
  };
}
